import { useState, type ReactNode } from 'react';
import { Clock3, FolderOutput, Hand, LoaderCircle, Zap } from 'lucide-react';
import { ExportDayStrip } from './ExportDayStrip';
import { captionOf, heldLine } from './export-timing-words';
import type { PaigeExportTiming, PaigeExportWhen } from './export-types';
import { useExportTiming } from './use-export-timing';
import './export-history.css';

/**
 * When the job's documents leave for its destinations (parity rows E-22, E-23), in Job setup › Export: CapturePoint's
 * three choices over its 24-hour strip (As soon as they're ready, Once a day at a time, Only when I say), Pause exports,
 * which holds everything without removing a destination, and Export now with what is ready beside it. The reading and
 * Paige's own copy never wait; only the sends to the destinations do, and they wait in Paige, never lost. Saves at
 * once through the job's channel. Renders nothing when the workbench is not on a Paige job.
 */
export function ExportTimingCard() {
  const bridge = window.paigeWorkbench;
  const { timing, busy, note, save, exportNow } = useExportTiming(bridge?.exports);
  if (!timing) return null;
  const locked = busy !== null || bridge?.readOnly === true || bridge?.runsJobs === false;
  const choose = (when: PaigeExportWhen, at = timing.at ?? '18:00') =>
    void save({ when, ...(when === 'daily' ? { at } : {}), paused: timing.paused });
  return (
    <div className="setup-map-setting paige-export-timing">
      <Clock3 size={22} aria-hidden="true" />
      <span>
        <strong>When documents leave</strong>
        <small>{captionOf(timing)}</small>
        <Choices timing={timing} locked={locked} onChoose={choose} />
        <ExportDayStrip when={timing.when} at={timing.at} paused={timing.paused} />
        <PauseSwitch
          timing={timing}
          locked={locked}
          onToggle={(paused) =>
            void save({ when: timing.when, ...(timing.at ? { at: timing.at } : {}), paused })
          }
        />
        <ExportNowRow
          timing={timing}
          sending={busy === 'sending'}
          locked={locked}
          onSend={() => void exportNow()}
        />
        {note && (
          <small className="paige-export-timing-note" role="status">
            {note}
          </small>
        )}
      </span>
    </div>
  );
}

function Choices(props: {
  timing: PaigeExportTiming;
  locked: boolean;
  onChoose: (when: PaigeExportWhen, at?: string) => void;
}) {
  const { timing, locked, onChoose } = props;
  const [draftAt, setDraftAt] = useState(timing.at ?? '18:00');
  const chip = (value: PaigeExportWhen, icon: ReactNode, label: string) => (
    <button
      type="button"
      disabled={locked}
      aria-pressed={timing.when === value}
      className={`export-chip${timing.when === value ? ' selected' : ''}`}
      onClick={() => onChoose(value, draftAt)}
    >
      {icon}
      {label}
    </button>
  );
  return (
    <div className="export-chips" role="group" aria-label="When documents leave">
      {chip('ready', <Zap size={16} aria-hidden="true" />, 'As soon as they’re ready')}
      {chip('daily', <Clock3 size={16} aria-hidden="true" />, 'Once a day')}
      {chip('manual', <Hand size={16} aria-hidden="true" />, 'Only when I say')}
      {timing.when === 'daily' && (
        <label className="export-time">
          <span>at</span>
          <input
            type="time"
            disabled={locked}
            value={draftAt}
            onChange={(event) => setDraftAt(event.target.value)}
            onBlur={() => draftAt && draftAt !== timing.at && onChoose('daily', draftAt)}
            aria-label="Time of day, in your company's time zone"
          />
        </label>
      )}
    </div>
  );
}

/** Pause exports: a switch, with what it does and does not do in one line. */
function PauseSwitch(props: {
  timing: PaigeExportTiming;
  locked: boolean;
  onToggle: (paused: boolean) => void;
}) {
  const { timing, locked, onToggle } = props;
  return (
    <label className="export-well export-switch-row paige-export-pause">
      <span>
        <b>Pause exports</b>
        <small>
          Holds every document here without removing a destination. Reading goes on; resume and they
          leave.
        </small>
      </span>
      <input
        type="checkbox"
        role="switch"
        className="export-switch"
        checked={timing.paused}
        disabled={locked}
        onChange={(event) => onToggle(event.target.checked)}
        aria-label="Pause exports"
      />
    </label>
  );
}

/** What waits, and Export now while something does and exports are not paused. */
function ExportNowRow(props: {
  timing: PaigeExportTiming;
  sending: boolean;
  locked: boolean;
  onSend: () => void;
}) {
  const { timing, sending, locked, onSend } = props;
  const line = heldLine(timing);
  const offered = !timing.paused && (timing.when !== 'ready' || timing.held > 0);
  if (!line && !offered) return null;
  return (
    <div className={`paige-export-now${timing.paused ? ' is-paused' : ''}`}>
      <small role="status">{line ?? 'Nothing ready yet'}</small>
      {offered && (
        <ExportNowButton held={timing.held} sending={sending} locked={locked} onSend={onSend} />
      )}
    </div>
  );
}

/** Export now, with how many are ready (CapturePoint's "12 documents ready" beside it); disabled while nothing is. */
function ExportNowButton(props: {
  held: number;
  sending: boolean;
  locked: boolean;
  onSend: () => void;
}) {
  const { held, sending, locked, onSend } = props;
  const label = held > 0 ? `Export now (${held.toLocaleString()} ready)` : 'Export now';
  return (
    <button
      type="button"
      className="secondary-button small"
      disabled={locked || held === 0}
      onClick={onSend}
    >
      {sending ? (
        <LoaderCircle className="paige-spin" size={15} aria-hidden="true" />
      ) : (
        <FolderOutput size={15} aria-hidden="true" />
      )}
      {sending ? 'Exporting…' : label}
    </button>
  );
}
